/**
 * useCoPilotHistory - Manage chat history state and persistence
 * 
 * SIMPLIFIED ARCHITECTURE:
 * - Pending actions are stored IN the chat message itself (pending_action.tag)
 * - No separate pending_actions state needed
 * - Action outcomes logged as new message + clear pending_action tag
 * - Last message with pending_action.tag = "PENDING_REVIEW" = action pending
 */
import { useState, useCallback, useRef } from 'react';
import { unifiedCopilotApi } from '../../../services/api';

export const useCoPilotHistory = (contextType, contextId) => {
  const [messages, setMessages] = useState([]);
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const loadingRef = useRef(false);

  // Load chat history from backend
  const loadHistory = useCallback(async () => {
    if (!contextType || !contextId || loadingRef.current) return;
    
    loadingRef.current = true;
    setLoading(true);
    setError(null);
    
    try {
      const res = await unifiedCopilotApi.getHistory(contextType, contextId, {
        limit: 100
      });
      const history = res.data;
      
      setMessages(history?.messages || []);
      setSummaries(history?.summaries || []);
    } catch (err) {
      console.error('Failed to load chat history:', err);
      setError(err);
      setMessages([]);
      setSummaries([]);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [contextType, contextId]);

  /**
   * Add message to local UI state only (optimistic update).
   * Backend handles persistence via /chat endpoint.
   */
  const addLocalMessage = useCallback((role, content, metadata = {}) => {
    const message = {
      role,
      content,
      timestamp: new Date().toISOString(),
      ...metadata
    };
    setMessages(prev => [...prev, message]);
    return message;
  }, []);

  /**
   * Remove the last message matching criteria (for failed turn recovery).
   */
  const removeLastMessage = useCallback((role, content) => {
    setMessages(prev => {
      const idx = prev.findLastIndex(m => m.role === role && m.content === content);
      if (idx === -1) return prev;
      return [...prev.slice(0, idx), ...prev.slice(idx + 1)];
    });
  }, []);

  /**
   * Resolve an action (accept/dismiss).
   * Replaces the action in pending_action.actions with an outcome object.
   * Same render path for immediate UI and reload from backend.
   * 
   * @param {string} outcomeType - 'accepted' or 'dismissed'
   * @param {string} summary - Human-readable summary of what was resolved
   * @param {object} metadata - Additional metadata (author_id, author_name, etc.)
   * @param {object} options - Required settings
   * @param {number} options.actionIndex - Index of the action in the actions array
   */
  const logActionOutcome = useCallback(async (outcomeType, summary, metadata = {}, options = {}) => {
    if (!contextType || !contextId) return null;
    
    const { actionIndex, messageId } = options;
    
    // Build outcome object
    const outcome = {
      type: 'OUTCOME',
      outcome: outcomeType,
      summary,
      timestamp: new Date().toISOString(),
      author_name: metadata.author_name || 'You'
    };
    
    // Update local state - find message by message_id
    setMessages(prev => {
      const targetIndex = prev.findIndex(m => m.message_id === messageId);
      
      if (targetIndex === -1 || typeof actionIndex !== 'number') {
        return prev;
      }
      
      // Update the action in place with outcome
      return prev.map((m, i) => {
        if (i !== targetIndex) return m;
        
        const actions = m.pending_action.actions || [];
        const updatedActions = actions.map((a, idx) => 
          idx === actionIndex ? outcome : a
        );
        
        // Check if all actions are now resolved
        const allResolved = updatedActions.every(a => a.type === 'OUTCOME');
        
        return {
          ...m,
          pending_action: {
            ...m.pending_action,
            actions: updatedActions,
            tag: allResolved ? null : 'PENDING_REVIEW'
          }
        };
      });
    });
    
    // Persist to backend using message_id
    try {
      if (messageId && typeof actionIndex === 'number') {
        await unifiedCopilotApi.resolveAction(
          contextType,
          contextId,
          messageId,
          actionIndex,
          outcomeType,
          summary
        );
      }
    } catch (err) {
      console.error('Failed to persist action resolution:', err);
    }
    
    return outcome;
  }, [contextType, contextId]);

  /**
   * Sync messages from backend response after successful chat.
   * Replaces optimistic state with authoritative backend state.
   */
  const syncFromBackend = useCallback((userMsg, assistantMsg) => {
    setMessages(prev => {
      // Remove the optimistic user message (last user message)
      const withoutOptimistic = prev.filter((m, i) => 
        !(i === prev.length - 1 && m.role === 'user')
      );
      // Add the authoritative messages from backend
      return [...withoutOptimistic, userMsg, assistantMsg];
    });
  }, []);

  // Clear chat history (user-scoped)
  const clearHistory = useCallback(async () => {
    if (!contextType || !contextId) return;
    
    try {
      await unifiedCopilotApi.clearHistory(contextType, contextId);
      setMessages([]);
    } catch (err) {
      console.error('Failed to clear history:', err);
      throw err;
    }
  }, [contextType, contextId]);

  // Add conversation summary
  const addSummary = useCallback(async (summary, actions = []) => {
    if (!contextType || !contextId) return;
    
    try {
      await unifiedCopilotApi.addSummary(contextType, contextId, summary, actions);
    } catch (err) {
      console.error('Failed to save summary:', err);
    }
  }, [contextType, contextId]);

  return {
    messages,
    setMessages,
    summaries,
    loading,
    error,
    loadHistory,
    addLocalMessage,
    removeLastMessage,
    logActionOutcome,
    syncFromBackend,
    clearHistory,
    addSummary
  };
};

export default useCoPilotHistory;
